import { useCallback } from 'react';

/**
 * useClipboard — copy text to clipboard with a fallback for older browsers.
 *
 * Usage:
 *   const { copy } = useClipboard({ onSuccess: (id) => setCopiedId(id) });
 *   copy('text to copy', 'my-button-id');
 */
export function useClipboard({ onSuccess } = {}) {
  const execCommandFallback = useCallback((text) => {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;';
      document.body.appendChild(el);
      el.select();
      el.setSelectionRange(0, el.value.length);
      let ok = false;
      try { ok = document.execCommand('copy'); } catch (_e) { ok = false; }
      document.body.removeChild(el);
      return ok;
    } catch (_e) { return false; }
  }, []);

  const copy = useCallback((text, id = 'default') => {
    const succeed = () => onSuccess?.(id);
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text)
          .then(succeed)
          .catch(() => { execCommandFallback(text) && succeed(); });
      } else {
        execCommandFallback(text) && succeed();
      }
    } catch (_e) { /* silent */ }
  }, [execCommandFallback, onSuccess]);

  return { copy };
}
